// Хранилище токенов OIDC V2 + refresh-flow.
//
// В Authorization уходит id_token: он содержит email/name/picture/sub —
// именно то, что middleware/auth.ts на бэке ожидает в payload. Audience у
// id_token и access_token одинаковая (`lplanner`), JWKS тот же — для бэка
// разницы нет, какой из двух валидировать.
//
// access_token нужен только если когда-нибудь будем вызывать чужие API
// (например, /userinfo auth-v2). Пока используется только для refresh-flow.

const ID_KEY = "lplanner.token"; // оставлен прежним для обратной совместимости
const ACCESS_KEY = "lplanner.access_token";
const REFRESH_KEY = "lplanner.refresh_token";
const EXPIRES_KEY = "lplanner.expires_at";

const AUTH_URL = import.meta.env.VITE_AUTH_URL ?? "https://auth-v2.gildiya7.ru";
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID ?? "lplanner";
// Refresh заранее, чтобы id_token не истёк прямо между проверкой и fetch'ем.
const REFRESH_LEEWAY_MS = 60 * 1000;

export interface TokenSet {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

const listeners = new Set<() => void>();
const emit = (): void => {
  for (const l of listeners) l();
};

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota / privacy mode */
  }
}
function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

let inFlightRefresh: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refresh = safeGet(REFRESH_KEY);
  if (!refresh) return false;
  try {
    const res = await fetch(`${AUTH_URL}/oauth/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        refresh_token: refresh,
      }),
    });
    if (!res.ok) {
      // 400 invalid_grant = refresh умер или украден; обнуляем сессию.
      tokenStore.clear();
      return false;
    }
    const tokens = (await res.json()) as TokenSet;
    tokenStore.set(tokens);
    return true;
  } catch {
    return false;
  }
}

export const tokenStore = {
  // id_token для Authorization. Сохранён сигнатурой get(): string | null,
  // чтобы App.tsx / api/client.ts не пришлось переписывать.
  get(): string | null {
    return safeGet(ID_KEY);
  },
  getRefresh(): string | null {
    return safeGet(REFRESH_KEY);
  },
  getExpiresAt(): number | null {
    const raw = safeGet(EXPIRES_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  },
  /** Сохраняет полный набор токенов после успешного /oauth/token. */
  set(tokens: TokenSet): void {
    safeSet(ID_KEY, tokens.id_token);
    safeSet(ACCESS_KEY, tokens.access_token);
    safeSet(REFRESH_KEY, tokens.refresh_token);
    safeSet(EXPIRES_KEY, String(Date.now() + tokens.expires_in * 1000));
    emit();
  },
  /** Только для AUTH_DEV_BYPASS: положить «токен» без refresh/expires —
   *  ensureFresh без refresh_token не сработает и вернёт false, fetch уйдёт
   *  с этим Bearer, а сервер в AUTH_DEV_BYPASS=true его игнорирует. */
  setBypass(token: string): void {
    safeSet(ID_KEY, token);
    safeRemove(ACCESS_KEY);
    safeRemove(REFRESH_KEY);
    safeRemove(EXPIRES_KEY);
    emit();
  },
  clear(): void {
    safeRemove(ID_KEY);
    safeRemove(ACCESS_KEY);
    safeRemove(REFRESH_KEY);
    safeRemove(EXPIRES_KEY);
    emit();
  },
  /** Полноценный выход: отзываем refresh-токен в auth-v2 (RFC 7009) — после
   *  этого его нельзя использовать ни в одной вкладке, даже если refresh
   *  ещё лежит у пользователя в localStorage где-то ещё. Сразу же чистим
   *  локальное хранилище. Revoke идёт fire-and-forget: если сеть упала,
   *  локальный logout всё равно произойдёт. */
  async logout(): Promise<void> {
    const refresh = safeGet(REFRESH_KEY);
    this.clear();
    if (refresh) {
      try {
        await fetch(`${AUTH_URL}/oauth/revoke`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: refresh,
            token_type_hint: "refresh_token",
            client_id: CLIENT_ID,
          }),
        });
      } catch {
        /* сеть упала — пользователь уже разлогинен локально */
      }
    }
  },
  /**
   * Гарантирует свежий id_token: если до истечения меньше REFRESH_LEEWAY_MS,
   * делает refresh. Дедуплицирует параллельные вызовы — несколько fetch'ей,
   * которые одновременно увидели мёртвый токен, дёрнут refresh один раз.
   */
  async ensureFresh(): Promise<boolean> {
    if (!safeGet(ID_KEY) || !safeGet(REFRESH_KEY)) return false;
    const exp = this.getExpiresAt();
    if (exp !== null && Date.now() < exp - REFRESH_LEEWAY_MS) return true;
    if (!inFlightRefresh) {
      inFlightRefresh = performRefresh().finally(() => {
        inFlightRefresh = null;
      });
    }
    return inFlightRefresh;
  },
  /** Принудительный refresh — для retry на 401. */
  async refreshNow(): Promise<boolean> {
    if (!inFlightRefresh) {
      inFlightRefresh = performRefresh().finally(() => {
        inFlightRefresh = null;
      });
    }
    return inFlightRefresh;
  },
  /**
   * Subscribe to token changes within the same tab. Returns an unsubscribe.
   * Used by long-lived connections (SSE) so they can close their socket the
   * moment the user is logged out — without waiting for the next navigation
   * or 401 to tear the page down.
   */
  subscribe(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  },
};
